import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    computed,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ImageComponent } from '@interacta-shared/ui';
import { ThemeMode } from '@interacta-shared/util-common';
import { TranslatePipe } from '@ngx-translate/core';
import { AI_LOGO_WHITE_SRC, aiThemedAssetSrc } from '../../utils/ai-assets';

const PRESS_MIN_DURATION_MS = 250;

@Component({
    selector: 'interacta-ai-header-button',
    templateUrl: './ai-header-button.component.html',
    styleUrl: './ai-header-button.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ImageComponent, TranslatePipe, MatTooltipModule],
    host: {
        '[class.is-active]': 'active()',
        '[class.is-pressed]': 'isPressed()',
        '[class.is-focused]': 'isFocusVisible()',
    },
})
export class AIHeaderButtonComponent {
    readonly active = input(false);
    readonly describedBy = input<string>();
    readonly themeMode = input<ThemeMode>('light');

    readonly clicked = output();

    readonly isPressed = signal(false);
    readonly isFocusVisible = signal(false);

    readonly logoSrc = computed(() =>
        this.active()
            ? AI_LOGO_WHITE_SRC
            : aiThemedAssetSrc('logo', this.themeMode()),
    );

    private releaseTimeout?: ReturnType<typeof setTimeout>;

    constructor() {
        inject(DestroyRef).onDestroy(() => clearTimeout(this.releaseTimeout));
    }

    /*
     * :focus-visible cannot be read from the glow, which sits outside the button,
     * and :has() is not available on every supported browser: the state is mirrored
     * onto the host instead.
     */
    updateFocusVisible(event: FocusEvent): void {
        this.isFocusVisible.set(
            (event.target as HTMLElement).matches(':focus-visible'),
        );
    }

    /*
     * A key press delivers keydown, click and keyup within the same millisecond,
     * so releasing on keyup leaves the 150ms press transition no time to be seen.
     * The state is held for a minimum instead, and auto-repeat rearms it while the
     * key stays down. Pointer presses keep using :active.
     */
    startPress(): void {
        this.isPressed.set(true);
        clearTimeout(this.releaseTimeout);
        this.releaseTimeout = setTimeout(
            () => this.isPressed.set(false),
            PRESS_MIN_DURATION_MS,
        );
    }
}
